import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { FloorPlanIcon, ImageIcon } from "@gemini/icons";
import { useI18n } from "@/contexts/i18n";
import { useModal } from "@/hooks";
import { ModalIds } from "@/constants";
import { useImages } from "@/gallery/hooks";
import { useFloorplans } from "@/components/Sections/Floorplan";
import { TAB_ITEMS } from "@/components/UI/Tabs/contants";
export const GalleryContext = createContext(null);
export const GalleryProvider = ({ children }) => {
    const { t } = useI18n();
    const { imagesFullscreen, masonryImages, imagesCount } = useImages();
    const { floorplansFullscreen, masonryFloorplans, floorplansCount } = useFloorplans();
    const [activeTab, setActiveTab] = useState(imagesCount > 0 ? TAB_ITEMS.IMAGES : TAB_ITEMS.FLOORPLANS);
    const [initialStepIndex, setInitialStepIndex] = useState(0);
    useEffect(() => {
        setActiveTab(imagesCount > 0 ? TAB_ITEMS.IMAGES : TAB_ITEMS.FLOORPLANS);
    }, [imagesCount]);
    const updateTab = useCallback((tab) => {
        setActiveTab(tab);
    }, []);
    const onTabChange = useCallback((tab) => {
        if (tab === activeTab)
            return;
        setInitialStepIndex(0); // when a tab changes, we want to reset the initialStepIndex of the carousel to the first item, this value is used to initialize the currentStep of the carousel
        setActiveTab(tab);
    }, [activeTab]);
    const tabs = useMemo(() => {
        const availableTabs = [
            {
                label: t("gallery.tabs.images.label"),
                Icon: ImageIcon,
                name: TAB_ITEMS.IMAGES,
                data: imagesFullscreen,
            },
            {
                label: t("gallery.tabs.floorplans.label"),
                Icon: FloorPlanIcon,
                name: TAB_ITEMS.FLOORPLANS,
                data: floorplansFullscreen,
            },
        ].filter((tab) => Array.isArray(tab.data) && tab.data.length > 0);
        return availableTabs.length > 1 ? availableTabs : null;
    }, [floorplansFullscreen, imagesFullscreen, t]);
    const updateInitialStepIndex = (index) => {
        setInitialStepIndex(index);
    };
    const carouselItems = activeTab === TAB_ITEMS.IMAGES ? imagesFullscreen : floorplansFullscreen;
    const masonryItems = activeTab === TAB_ITEMS.IMAGES ? masonryImages : masonryFloorplans;
    const galleryCount = activeTab === TAB_ITEMS.IMAGES ? imagesCount : floorplansCount;
    const { isOpen: isMasonryModalOpen, open: openMasonryModal, toggle: toggleMasonryModal, close: closeMasonryModal, openAndReplace: openMasonryAndReplace, } = useModal(false, ModalIds.MASONRY);
    const { isOpen: isSliderModalOpen, open: openSliderModal, toggle: toggleSliderModal, close: closeSliderModal, } = useModal(false, ModalIds.SLIDER);
    const value = useMemo(() => ({
        isMasonryModalOpen,
        openMasonryModal,
        toggleMasonryModal,
        closeMasonryModal,
        isSliderModalOpen,
        openSliderModal,
        toggleSliderModal,
        closeSliderModal,
        openMasonryAndReplace,
        activeTab,
        updateTab,
        tabs,
        onTabChange,
        carouselItems,
        masonryItems,
        galleryCount,
        updateInitialStepIndex,
        initialStepIndex,
    }), [
        isMasonryModalOpen,
        openMasonryModal,
        toggleMasonryModal,
        closeMasonryModal,
        isSliderModalOpen,
        openSliderModal,
        toggleSliderModal,
        closeSliderModal,
        openMasonryAndReplace,
        activeTab,
        updateTab,
        tabs,
        onTabChange,
        carouselItems,
        masonryItems,
        galleryCount,
        initialStepIndex,
    ]);
    return (_jsx(GalleryContext.Provider, { value: value, children: children }));
};
/**
 * useGalleryContext handles the logic for opening & closing both the masonry and slider modals, as well as handling the tab logic a active elements to be displayed in the modals. It also returns formatted data for the masonry and carousel.
 * @returns modals handlers
 * @returns `activeTab`
 * @returns `updateTab`
 * @returns `carouselItems`: formatted images or floorplans for carousel
 * @returns `masonryItems`: formatted images or floorplans for masonry
 */
export const useGalleryContext = () => {
    const context = useContext(GalleryContext);
    if (context === undefined) {
        throw new Error("useGalleryContext must be used within a GalleryProvider");
    }
    return context;
};
